import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/** Dialog input. `countdownSeconds` is the shared session's remaining lifetime. */
export interface InactivityWarningData {
  countdownSeconds: number;
}

/**
 * ERP-15430 — V2's cross-app inactivity warning. Mirrors V4's
 * `InactivityWarningComponent`. Opened by `AppComponent` when the shared
 * `linq.session.active` cookie's remaining lifetime (surfaced by the 30s
 * heartbeat poll) drops under the warning threshold. The countdown uses
 * wall-clock math so a throttled background tab stays accurate, and is
 * clamped at 0 so it can never render a negative time.
 *
 * Close results:
 *   - 'stay'    — user clicked "Stay Logged In".
 *   - 'logout'  — user clicked "Logout Now" (unconditional logout).
 *   - 'timeout' — countdown reached 0 (AppComponent verifies the shared
 *                 cookie before logging out, in case a peer app refreshed it).
 */
@Component({
  selector: 'app-inactivity-warning',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatDialogModule],
  templateUrl: './inactivity-warning.component.html',
  styleUrls: ['./inactivity-warning.component.scss'],
})
export class InactivityWarningComponent implements OnInit, OnDestroy {
  countdown: number;
  private destroy$ = new Subject<void>();

  /** Wall-clock time (ms) at which auto-logout should fire. */
  private logoutAtTime: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InactivityWarningData,
    private readonly dialogRef: MatDialogRef<InactivityWarningComponent>,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.countdown = this.data.countdownSeconds;
    this.logoutAtTime = Date.now() + this.data.countdownSeconds * 1000;
  }

  ngOnInit(): void {
    // Wall-clock countdown so it stays accurate even when the browser
    // throttles timers in a background tab.
    timer(0, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.countdown = Math.max(0, Math.ceil((this.logoutAtTime - Date.now()) / 1000));
        // V2 is a legacy Frames-hosted shell where Angular's zone-based change
        // detection is largely dormant (the main UI is jQuery/Frames). A dialog
        // opened from the heartbeat-poll callback doesn't reliably get a CD tick
        // each second, so force a local render to keep the countdown visibly
        // ticking. ERP-15430.
        this.cdr.detectChanges();
        if (this.countdown <= 0) {
          this.dialogRef.close('timeout');
        }
      });
  }

  /** Formats the countdown as M:SS. */
  get formattedCountdown(): string {
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  stay(): void {
    this.dialogRef.close('stay');
  }

  logout(): void {
    this.dialogRef.close('logout');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
